import React, { useState, useMemo } from 'react';
import { Search, Edit } from 'lucide-react';
import { Device } from '../lib/supabase';

interface DeviceSelectorProps {
  devices: Device[];
  selectedDevice: string | null;
  onSelectDevice: (deviceId: string | null) => void;
  onEditDevice: () => void;
}

export default function DeviceSelector({ 
  devices, 
  selectedDevice, 
  onSelectDevice, 
  onEditDevice 
}: DeviceSelectorProps) {
  const [searchMode, setSearchMode] = useState<'nickname' | 'imei'>('nickname');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredDevices = useMemo(() => {
    if (!searchTerm) return devices;
    
    const term = searchTerm.toLowerCase();
    return devices.filter(device => {
      if (searchMode === 'nickname') {
        return device.device_nickname?.toLowerCase().includes(term) || 
               device.device_id.toLowerCase().includes(term);
      } else {
        return device.device_id.toLowerCase().includes(term);
      }
    });
  }, [devices, searchTerm, searchMode]);

  return (
    <div className="sidebar-section">
      <label className="sidebar-label">
        Device Filter
      </label>
      
      {/* Search Mode Tabs */}
      <div className="flex bg-white/50 rounded-lg p-1 mb-3">
        <button
          onClick={() => setSearchMode('nickname')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
            searchMode === 'nickname'
              ? 'bg-black text-traqpack-yellow shadow-sm'
              : 'text-black/70 hover:text-black'
          }`}
        >
          By Name
        </button>
        <button
          onClick={() => setSearchMode('imei')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
            searchMode === 'imei'
              ? 'bg-black text-traqpack-yellow shadow-sm'
              : 'text-black/70 hover:text-black'
          }`}
        >
          By IMEI
        </button>
      </div>

      {/* Search Input */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-black/50" />
        <input
          type="text"
          placeholder={searchMode === 'nickname' ? 'Search by device name...' : 'Search by IMEI...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-white border-2 border-black/20 rounded-lg text-black placeholder-black/50 focus:ring-2 focus:ring-black focus:border-black"
        />
      </div>

      {/* Device Dropdown */}
      <select
        value={selectedDevice || ''}
        onChange={(e) => onSelectDevice(e.target.value || null)}
        className="block w-full px-4 py-3 bg-white border-2 border-black/20 rounded-xl font-medium focus:ring-2 focus:ring-black focus:border-black transition-all mb-3"
        size={searchTerm ? Math.min(filteredDevices.length + 1, 6) : 1}
      >
        <option value="">All Devices</option>
        {filteredDevices.map((device) => (
          <option key={device.id} value={device.id}>
            {device.device_nickname 
              ? `${device.device_nickname} • ${device.device_id}` 
              : `Device ${device.id} • ${device.device_id}`}
          </option>
        ))}
      </select>

      {/* Edit Button */}
      {selectedDevice && (
        <button
          onClick={onEditDevice}
          className="w-full px-4 py-3 bg-black text-traqpack-yellow rounded-xl font-bold hover:bg-black/90 transition-all flex items-center justify-center shadow-sm hover:shadow-md"
          title="Edit device name"
        >
          <Edit className="w-4 h-4 mr-2" />
          Edit Device
        </button>
      )}
    </div>
  );
}